import { Box, Flex, type FlexProps } from '@chakra-ui/react';
import { type ReactNode } from 'react';

interface SplitAuthShellProps {
  /** Form body, rendered inside the centered scrollable column. */
  children: ReactNode;
  /** Right panel (manages its own hide below `lg`); rendered as a slot. */
  brandPanel: ReactNode;
  /** Max width of the centered form column (e.g. '392px' / '438px'). */
  maxW: string;
  /** Optional row pinned above the scrollable form column (logo / cross-link). */
  topBar?: ReactNode;
  /** Optional row pinned below the scrollable form column (copyright). */
  footer?: ReactNode;
  /** Vertical padding of the form pane. */
  py?: FlexProps['py'];
}

/**
 * Full-viewport split-screen shell shared by the login and brand-onboarding
 * pages: a cream form pane on the left and a dark `brandPanel` slot on the right
 * (the panel hides itself below `lg`, so the form goes full-width on mobile).
 *
 * The centered form column uses `minH={0}` + `my="auto"` so it centers when the
 * content fits and scrolls from the top (no clipping) when it overflows on small
 * screens. Keeping that fix here means it lives in exactly one place.
 */
export const SplitAuthShell = ({
  children,
  brandPanel,
  maxW,
  topBar,
  footer,
  py = { base: 6, md: '34px' },
}: SplitAuthShellProps) => (
  <Flex
    w="full"
    minH="100dvh"
    h={{ base: 'auto', lg: '100dvh' }}
    overflow={{ base: 'visible', lg: 'hidden' }}
    bg="background"
    color="dark"
  >
    {/* FORM SIDE */}
    <Flex flex={1} minW={0} direction="column" bg="background" px={{ base: 5, md: '48px' }} py={py}>
      {topBar}

      {/* Centered form column. Below lg the whole page scrolls, so the footer
          always follows the form instead of sitting over its last lines; from
          lg the column scrolls on its own beside the fixed brand panel. */}
      <Flex
        flex={1}
        minH={0}
        direction="column"
        align="center"
        py={6}
        overflowY={{ base: 'visible', lg: 'auto' }}
      >
        <Box w="full" maxW={maxW} my="auto">
          {children}
        </Box>
      </Flex>

      {footer}
    </Flex>

    {/* BRAND SIDE (slot) */}
    {brandPanel}
  </Flex>
);
